import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2, Command, Plus, Video, Users, FileText, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface SearchResult {
  id: string;
  type: string;
  title: string;
  description: string;
  metadata: Record<string, any>;
  url: string;
  created_at: string | null;
}

interface FederatedSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'course': return '📚';
    case 'video': return '🎥';
    case 'instructor': return '👩‍🏫';
    case 'proposal': return '📋';
    case 'survey': return '📊';
    default: return '📄';
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'course': return 'bg-blue-100 text-blue-800';
    case 'video': return 'bg-purple-100 text-purple-800';
    case 'instructor': return 'bg-green-100 text-green-800';
    case 'proposal': return 'bg-orange-100 text-orange-800';
    case 'survey': return 'bg-pink-100 text-pink-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const quickActions = [
  { label: "Create Course", icon: Plus, path: "/course-builder" },
  { label: "Assign Video", icon: Video, path: "/video-library" },
  { label: "Manage Users", icon: Users, path: "/manage-users" },
  { label: "View Reports", icon: FileText, path: "/admin-reporting" },
];

export function FederatedSearch({ isOpen, onClose }: FederatedSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [recentItems, setRecentItems] = useState<SearchResult[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Load recent items from localStorage
  useEffect(() => {
    if (isOpen) {
      const recent = localStorage.getItem('recentSearchItems');
      if (recent) {
        setRecentItems(JSON.parse(recent).slice(0, 5));
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setHasSearched(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim().length >= 2) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query.trim());
      }, 300);
    } else {
      setResults([]);
      setHasSearched(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    if (searchQuery.length < 2) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const { data, error } = await supabase.functions.invoke('federated-search', {
        body: { query: searchQuery, limit: 20 }
      });

      if (error) throw error;

      setResults(data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search Error",
        description: "Failed to perform search. Please try again.",
        variant: "destructive"
      });
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    // Save to recent items
    const recent = localStorage.getItem('recentSearchItems');
    const recentList: SearchResult[] = recent ? JSON.parse(recent) : [];
    const updated = [result, ...recentList.filter(r => r.id !== result.id)].slice(0, 10);
    localStorage.setItem('recentSearchItems', JSON.stringify(updated));
    
    onClose();
    setQuery("");
    setResults([]);
    setHasSearched(false);
  };

  const handleQuickAction = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Command className="h-5 w-5" />
              Command Palette
            </div>
            <Badge variant="outline" className="text-xs">
              <Command className="h-3 w-3 mr-1" />K
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search courses, videos, instructors, and more..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 pr-10"
              autoFocus
            />
            {query && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Quick Actions */}
          {!query && !hasSearched && (
            <div className="space-y-3">
              <div className="text-sm font-medium flex items-center gap-2">
                <Command className="h-4 w-4" />
                Quick Actions
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action) => (
                  <Button
                    key={action.path}
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => handleQuickAction(action.path)}
                  >
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.label}
                  </Button>
                ))}
              </div>
              
              {/* Recent Items */}
              {recentItems.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Recent
                  </div>
                  <div className="space-y-2">
                    {recentItems.map((item) => (
                      <Link
                        key={item.id}
                        to={item.url}
                        onClick={() => handleResultClick(item)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                      >
                        <span className="text-lg">{getTypeIcon(item.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{item.title}</div>
                          <div className="text-xs text-muted-foreground truncate">{item.type}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Search Results */}
          <ScrollArea className="h-96">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Searching...</span>
              </div>
            ) : hasSearched && results.length === 0 ? (
              <div className="text-center py-8">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <h3 className="text-lg font-medium mb-1">No results found</h3>
                <p className="text-sm text-muted-foreground">
                  Try different keywords or check spelling
                </p>
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Found {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
                </div>
                {results.map((result, index) => (
                  <Card key={`${result.type}-${result.id}-${index}`} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <Link 
                        to={result.url} 
                        onClick={() => handleResultClick(result)}
                        className="block space-y-2"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{getTypeIcon(result.type)}</span>
                              <Badge className={`text-xs ${getTypeColor(result.type)}`}>
                                {result.type}
                              </Badge>
                            </div>
                            <h4 className="font-medium text-foreground line-clamp-2">
                              {result.title}
                            </h4>
                            {result.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                {result.description}
                              </p>
                            )}
                            
                            {/* Metadata display */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {result.metadata.duration_hours && (
                                <Badge variant="outline" className="text-xs">
                                  {result.metadata.duration_hours}h
                                </Badge>
                              )}
                              {result.metadata.category && (
                                <Badge variant="outline" className="text-xs">
                                  {result.metadata.category.replace('_', ' ')}
                                </Badge>
                              )}
                              {result.metadata.department && (
                                <Badge variant="outline" className="text-xs">
                                  {result.metadata.department}
                                </Badge>
                              )}
                              {result.metadata.status && (
                                <Badge variant="outline" className="text-xs">
                                  {result.metadata.status}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <h3 className="text-lg font-medium mb-1">Search Everything</h3>
                <p className="text-sm text-muted-foreground">
                  Find courses, videos, instructors, proposals, and more across the platform
                </p>
              </div>
            )}
          </ScrollArea>

          {/* Search Tips */}
          {!hasSearched && (
            <div className="bg-muted/50 p-3 rounded-lg">
              <div className="text-sm">
                <p className="font-medium mb-1">Search tips:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Search by title, description, or instructor name</li>
                  <li>• Use specific keywords for better results</li>
                  <li>• Results include courses, videos, instructors, and more</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}